import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Input, Row, Select, Space, Statistic, Table, Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { useListServerProcessesQuery } from '../../store/api';

dayjs.extend(relativeTime);
dayjs.locale('es');

const STATUS_COLORS = {
  ok: 'success',
  running: 'processing',
  error: 'error',
  missing: 'error',
  stopped: 'error',
  timeout: 'error',
  stale: 'warning',
  error_in_log: 'error',
  no_ssh: 'warning',
  unknown: 'default',
  warning: 'warning',
};

const STATUS_LABELS = {
  ok: 'OK',
  error: 'ERROR',
  missing: 'NO EXISTE',
  stopped: 'DETENIDO',
  timeout: 'TIMEOUT',
  stale: 'SIN ACTIVIDAD',
  error_in_log: 'ERROR EN LOG',
  no_ssh: 'SIN SSH',
  unknown: 'DESCONOCIDO',
  warning: 'ALERTA',
};

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

export default function CronjobsPanel() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const params = useMemo(() => ({
    page,
    limit: 25,
    is_active: '1',
    process_type: 'cron',
    ...(search ? { search } : {}),
    ...(status ? { last_status: status } : {}),
  }), [page, search, status]);

  const { data, isLoading, isFetching, refetch } = useListServerProcessesQuery(params, {
    pollingInterval: 60000,
  });

  const rows = data?.data || [];
  const total = data?.total || 0;
  const summary = data?.summary || {};
  const ok = Number(summary.ok || 0);
  const failing = Number(summary.failing || 0);
  const unknown = Number(summary.unknown || 0);
  const noSsh = Number(summary.no_ssh || 0);

  const columns = [
    {
      title: 'Servidor',
      key: 'servidor',
      width: 170,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.server_name || r.estado_servidor_nombre || r.server_ip || '—'}</div>
          <div style={{ fontSize: 11, color: '#8392a5' }}>{r.server_ip}</div>
        </div>
      ),
    },
    {
      title: 'Proyecto',
      dataIndex: 'proyecto_nombre',
      key: 'proyecto_nombre',
      width: 190,
      render: (v) => v || <span style={{ color: '#8392a5' }}>Sin proyecto</span>,
    },
    {
      title: 'Horario',
      dataIndex: 'schedule',
      key: 'schedule',
      width: 115,
      render: (v) => v ? <code style={{ fontSize: 11 }}>{v}</code> : '—',
    },
    {
      title: 'Qué hace',
      dataIndex: 'description_human',
      key: 'description_human',
      width: 360,
      render: (v) => v ? (
        <Tooltip title={v}>
          <span>{v}</span>
        </Tooltip>
      ) : <span style={{ color: '#8392a5' }}>Sin descripción</span>,
    },
    {
      title: 'Script',
      dataIndex: 'script_path',
      key: 'script_path',
      width: 260,
      ellipsis: true,
      render: (v) => v ? <Tooltip title={v}><code style={{ fontSize: 11 }}>{v}</code></Tooltip> : '—',
    },
    {
      title: 'Estado',
      dataIndex: 'last_status',
      key: 'last_status',
      width: 120,
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{STATUS_LABELS[v] || v || '—'}</Tag>,
    },
    {
      title: 'Último check',
      dataIndex: 'last_run',
      key: 'last_run',
      width: 130,
      render: (v) => v ? (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>
          <span>{dayjs(v).fromNow()}</span>
        </Tooltip>
      ) : '—',
    },
    {
      title: 'Último éxito',
      dataIndex: 'last_success_at',
      key: 'last_success_at',
      width: 130,
      render: (v) => v ? (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>
          <span>{dayjs(v).fromNow()}</span>
        </Tooltip>
      ) : '—',
    },
  ];

  return (
    <Card
      bordered={false}
      title={
        <Space>
          <ClockCircleOutlined style={{ color: '#0168fa' }} />
          <span>Cronjobs activos</span>
          <Tag>{total}</Tag>
          {isFetching ? <Tag color="processing">actualizando</Tag> : null}
        </Space>
      }
      extra={
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar script, servidor o descripción..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 280 }}
          />
          <Select
            allowClear
            placeholder="Todos los estados"
            value={status || undefined}
            options={STATUS_OPTIONS}
            onChange={(v) => { setStatus(v || ''); setPage(1); }}
            style={{ width: 170 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Actualizar
          </Button>
          <Button onClick={() => navigate('/procesos')}>Ver inventario completo</Button>
        </Space>
      }
    >
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Statistic title="Total activos" value={Number(summary.total || total)} prefix={<ClockCircleOutlined />} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="OK" value={ok} valueStyle={{ color: '#10b759' }} prefix={<CheckCircleOutlined />} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Con fallas" value={failing} valueStyle={{ color: '#dc3545' }} prefix={<WarningOutlined />} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Sin dato / sin SSH" value={unknown + noSsh} valueStyle={{ color: '#fd7e14' }} prefix={<QuestionCircleOutlined />} />
        </Col>
      </Row>

      <Table
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        scroll={{ x: 1500 }}
        pagination={{
          current: page,
          pageSize: 25,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} cronjobs activos`,
        }}
      />
    </Card>
  );
}
